package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.PosConnectionException
import com.evoapps.evoshift.domain.model.PosIntegration
import com.evoapps.evoshift.domain.model.PosProvider
import com.evoapps.evoshift.domain.model.PosRefusal
import com.evoapps.evoshift.domain.model.PosSyncStatus
import com.evoapps.evoshift.domain.model.PosSyncSummary
import com.evoapps.evoshift.domain.repository.PosIntegrationRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/**
 * SH-123 over Firebase: the connection is pos_integrations/{workplaceId} (written only by the
 * functions); connecting, disconnecting and "import now" are the connectPos / disconnectPos /
 * syncPosNow callables (functions/posImport.js). The API key goes to connectPos and nowhere else.
 */
class PosIntegrationRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : PosIntegrationRepository {
    private val json = Json { ignoreUnknownKeys = true }

    override fun observe(workplaceId: String): Flow<PosIntegration?> =
        firestore.collection(COLLECTION).document(workplaceId).safeSnapshots
            .map { doc -> if (doc.exists) doc.data<PosIntegrationDto>().toDomain(workplaceId) else null }
            .distinctUntilChanged()

    override suspend fun connect(workplaceId: String, provider: PosProvider, apiKey: String, vatRatePercent: Double): Result<PosIntegration> =
        call {
            val result = functions.call("connectPos", buildJsonObject {
                put("workplaceId", workplaceId)
                put("provider", provider.name)
                put("apiKey", apiKey.trim())
                put("vatRatePercent", vatRatePercent)
            })
            val reply = json.decodeFromJsonElement(ConnectReplyDto.serializer(), result)
            PosIntegration(workplaceId, provider, reply.merchantCode, reply.vatRatePercent)
        }

    override suspend fun disconnect(workplaceId: String): Result<Unit> = call {
        functions.call("disconnectPos", buildJsonObject { put("workplaceId", workplaceId) })
        Unit
    }

    override suspend fun syncNow(workplaceId: String): Result<PosSyncSummary> = call {
        val result = functions.call("syncPosNow", buildJsonObject { put("workplaceId", workplaceId) })
        json.decodeFromJsonElement(PosSyncSummaryDto.serializer(), result).toDomain()
    }

    /** A function's refusal with a known reason becomes a [PosConnectionException]. */
    private suspend fun <T> call(block: suspend () -> T): Result<T> = runCatching {
        try {
            block()
        } catch (e: CloudFunctionsException) {
            val reason = (e.details as? JsonObject)?.get("reason")?.jsonPrimitive?.contentOrNull
            throw PosRefusal.entries.firstOrNull { it.name == reason }?.let(::PosConnectionException) ?: e
        }
    }

    private companion object {
        const val COLLECTION = "pos_integrations"
    }
}

@Serializable
internal data class PosSyncSummaryDto(
    val written: Int = 0,
    val keptManual: Int = 0,
    val closed: Int = 0,
    val unchanged: Int = 0,
    val noRecord: Int = 0,
    val noSales: Int = 0,
    val from: String? = null,
    val to: String? = null
) {
    fun toDomain() = PosSyncSummary(written, keptManual, closed, unchanged, noRecord, noSales, from, to)
}

@Serializable
internal data class PosIntegrationDto(
    val provider: String = PosProvider.SUMUP.name,
    val merchantCode: String = "",
    val vatRatePercent: Double = 8.0,
    val enabled: Boolean = true,
    val connectedAt: String? = null,
    val lastSyncAt: String? = null,
    val lastSyncStatus: String? = null,
    val lastSyncSummary: PosSyncSummaryDto? = null
) {
    fun toDomain(workplaceId: String): PosIntegration? {
        if (!enabled) return null
        val known = PosProvider.entries.firstOrNull { it.name == provider } ?: return null
        return PosIntegration(
            workplaceId = workplaceId,
            provider = known,
            merchantCode = merchantCode,
            vatRatePercent = vatRatePercent,
            connectedAt = connectedAt,
            lastSyncAt = lastSyncAt,
            lastSyncStatus = PosSyncStatus.entries.firstOrNull { it.name == lastSyncStatus },
            lastSync = lastSyncSummary?.toDomain()
        )
    }
}

@Serializable
internal data class ConnectReplyDto(val merchantCode: String, val vatRatePercent: Double)
